import React, { useState, useRef } from "react";
import { videoShowcases } from "../mock";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { Play, Pause, ArrowRight, Volume2, VolumeX } from "lucide-react";
import ScrollCue from "../components/heider/ScrollCue";
import ConsultationCTA from "../components/heider/ConsultationCTA";
import BrokerFilms from "../components/heider/BrokerFilms";
import { Link } from "react-router-dom";

const ShowcaseCard = ({ item }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      document.querySelectorAll("[data-testid=video-showcases] video").forEach((o) => o !== v && o.pause());
      v.muted = muted; v.play(); setPlaying(true);
    } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (v) v.muted = !muted;
    setMuted(!muted);
  };
  return (
    <div className="reveal-scale group">
      <div className="relative overflow-hidden bg-black aspect-[16/10]">
        <video ref={ref} className="w-full h-full object-cover" poster={item.poster} loop playsInline onClick={toggle} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)}>
          <source src={item.video} type="video/mp4" />
        </video>
        <button onClick={toggle} data-testid={`showcase-play-${item.id}`} className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`} aria-label="Play">
          <span className="w-16 h-16 rounded-full bg-white/90 text-black flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            {playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
          </span>
        </button>
        <button onClick={toggleMute} data-testid={`showcase-mute-${item.id}`} aria-label={muted ? "Unmute" : "Mute"} className="absolute bottom-4 right-4 z-10 w-11 h-11 rounded-full bg-black/60 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-black/80 transition-colors">
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>
      <div className="pt-5">
        <h3 className="font-serif-display text-2xl text-white">{item.name}</h3>
        <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mt-1">{item.status}</p>
      </div>
    </div>
  );
};

const SellPage = () => {
  useReveal();
  return (
    <div className="page-in">
      {/* Video Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline poster="https://images.pexels.com/photos/7045701/pexels-photo-7045701.jpeg?auto=compress&cs=tinysrgb&w=1600">
          <source src="/home2.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative text-center text-white px-6">
          <h1 className="font-serif-display font-light text-5xl md:text-7xl lg:text-[6rem] leading-[1.05] clip-line">
            <span style={{ opacity: 0 }}>Your Perfect Buyer is Out There</span>
          </h1>
          <p className="mt-6 text-sm md:text-base tracking-luxe uppercase text-white/85 fade-in" style={{ animationDelay: "0.6s", opacity: 0 }}>We Will Find Them</p>
        </div>
        <ScrollCue />
      </section>

      <ConsultationCTA type="sell" />

      {/* Commanding results */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a] mb-8">Commanding Results. Uncompromising Service.</h2>
          <p className="text-neutral-500 text-[15px] leading-relaxed">
            When it comes to one of your most significant investments, relying on an automated estimate simply isn’t enough. At CAPITAL CORNER, we offer a complimentary market assessment grounded in real-time data, deep local expertise, and an understanding of what truly sets your property apart. Share a few key details with us, and our team will deliver an accurate, insightful valuation — along with the guidance you need to make confident decisions.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border border-[#1a1a1a] px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors">
            Discover Your Home's Market Value <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Broker Opens */}
      <section className="bg-[#f3f1ec] py-24 md:py-32 border-t border-black/5" data-testid="sell-broker-opens">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-6">No One Does Broker Opens Like Us</p>
          <p className="font-serif-display text-2xl md:text-[2rem] leading-relaxed text-[#2a2a2a] font-light">
            A broker open is not a party &mdash; it is a distribution strategy. Our films are built to stop the scroll, and our open houses put your home in front of buyers who are ready to move.
          </p>
        </div>
        <BrokerFilms />
        <div className="text-center mt-16">
          <Link to="/broker-opens" data-testid="sell-broker-opens-link" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border border-[#1a1a1a] px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors">
            Explore Broker Opens <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Video showcases */}
      <section className="bg-[#0d0d0d] text-white py-24 md:py-32">
        <div className="max-w-[1700px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">Cinematic Marketing</p>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light">Video Showcases</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[1300px] mx-auto" data-testid="video-showcases">
            {videoShowcases.map((item) => (
              <ShowcaseCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default SellPage;
